import {
	AdminPanelSettings, Article,
	FormatListBulleted,
	Help, Home, Menu as MenuIcon, Settings, SvgIconComponent, Tv,
	ViewCarousel,
} from '@mui/icons-material';
import { Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, MenuItemProps } from '@mui/material';
import { FunctionComponent, MouseEvent, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUnknownAuthState } from '../contexts/AuthContext';
import { GameBoardZIndex } from './game/GameBoardZIndex';

export const NavigationMenu: FunctionComponent = () => {
	const { isSignedIn, isAdmin } = useUnknownAuthState();
	const navigate = useNavigate();
	const location = useLocation();

	const enabled = isSignedIn && !['/answers', '/question-card'].includes(location.pathname);

	const [anchorEl, setAnchorEl] = useState<HTMLElement | undefined>(undefined);
	const isMenuOpen = Boolean(anchorEl);

	function goTo(path: string) {
		void navigate(path);
		closeMenu();
	}

	function openInNewTab(path: string) {
		window.open(path, '_blank');
		closeMenu();
	}

	function openMenu(event: MouseEvent<HTMLButtonElement>) {
		setAnchorEl(event.currentTarget);
	}

	function closeMenu() {
		setAnchorEl(undefined);
	}

	return enabled && <>
		<IconButton
			aria-label="navigation menu"
			aria-controls={isMenuOpen ? 'navigation-menu' : undefined}
			aria-haspopup="true"
			aria-expanded={isMenuOpen ? 'true' : undefined}
			onClick={openMenu}
			sx={{
				position: 'fixed',
				top: 16,
				left: 16,
				zIndex: GameBoardZIndex.FacilitatorUI,
				mixBlendMode: 'difference',
				color: 'white',
				'@media print': {
					display: 'none',
				},
			}}
		>
			<MenuIcon />
		</IconButton>

		<Menu
			id="navigation-menu"
			anchorEl={anchorEl}
			open={isMenuOpen}
			onClose={closeMenu}
		>
			<nav>
				<Item text='Home' onClick={() => goTo('/')} Icon={Home} />
				<Item text='New Game' onClick={() => goTo('/setup')} Icon={Tv} />
				<Item text='Answers' onClick={() => openInNewTab('/answers')} Icon={Article} />
				<Item text='Question Card' onClick={() => openInNewTab('/question-card')} Icon={Help} />
				<Item text='Settings' onClick={() => goTo('/settings')} Icon={Settings} />
				<Divider />
				<Item text='Admin'
					onClick={() => goTo('/admin')}
					Icon={AdminPanelSettings}
					disabled={!isAdmin} />
				<Item inset text='Questions' onClick={() => goTo('/admin/questions')} Icon={FormatListBulleted} />
				<Item inset text='Question Sets' onClick={() => goTo('/admin/question-sets')} Icon={ViewCarousel} />
			</nav>
		</Menu>
	</>;
};

type ItemProps = MenuItemProps & {
	text: string;
	Icon: SvgIconComponent;
	inset?: boolean;
};

const Item: FunctionComponent<ItemProps> = (props) => {
	const { Icon, text, inset = false, ...passThrough } = props;

	return <MenuItem {...passThrough} sx={[inset && { marginLeft: 2 }]}>
		<ListItemIcon>
			<Icon fontSize="small" />
		</ListItemIcon>
		<ListItemText>{text}</ListItemText>
	</MenuItem>;
};
